import React, { useState, useEffect, useContext, useCallback } from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { BoxInfo, getCustomerInfoCookieName } from 'shared/index'
import {
	FormBuilder,
	IAppStateControlProperty,
	formKeySelector,
} from '@tf/formbuilder'

import appConfig from 'app.config'
import {
	IKycData,
	CookieType,
	customerDataApiService,
	getErrorWithFullMessage,
	getMarket,
	getLanguageLocale,
	CircleSpinner,
	CustomerForm,
} from 'shared/index'
import { FormFactory } from 'shared/formBuilder/formFactory'
import AppConfigContext from 'shared/contexts/azureAppConfig'

import SspContext from 'selfServicePortal/contexts/SspContext'
import { Toggle } from 'original/constants/enums/ToggleTypes'

import './KycModal.scss'
import { ICustomer } from 'original/interfaces/i-api-loan'
import { CountryA3 } from 'shared/constants/countryTypes'

interface IKycProps {
	showModal: boolean
	data: IKycData
	handleClose: any
}

export const KycModal = ({ data }: IKycProps) => {
	const { t } = useTranslation(['translation', 'forms'])
	const [customer, setCustomer] = useState<IKycData>(data)
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<boolean>(false)
	const [showKycModal, setShowKycModal] = useState(true)
	const [showThankYouPage, setShowThankYouPage] = useState(false)

	const formSchema = FormFactory(
		data.customerType,
		{
			formName: 'basicPersonalData',
			blockName: 'KycData',
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
			submitButtonText: t('Save'),
		},
		data,
	)
	const valuesToSubmit = useSelector(
		formKeySelector(formSchema.formMeta.formName, 'controlsState'),
	)
	const errors = useSelector(
		formKeySelector(formSchema.formMeta.formName, 'errors'),
	)
	const updateCustomerObj = useCallback(
		(valuesToSubmit: IAppStateControlProperty) => {
			setCustomer({
				...customer,
				deposits:
					Object.entries(data.deposits).length !== 0
						? [
								{
									...customer.deposits[0],
									purposeOfDeposits:
										valuesToSubmit?.purposeOfDeposits.value,
									purposeOfDepositsOther:
										valuesToSubmit?.purposeOfDepositsOther
											?.value,
									moneySource:
										valuesToSubmit?.moneySource?.value,
									moneySourceOther:
										valuesToSubmit?.moneySourceOther?.value,
									depositOwner:
										valuesToSubmit?.depositOwner?.value,
									expectedDeposits:
										valuesToSubmit?.expectedDeposits?.value,
									expectedDepositsAmount:
										valuesToSubmit?.expectedDepositsAmount
											?.value,
									expectedWithdrawals:
										valuesToSubmit?.expectedWithdrawals
											?.value,
									expectedWithdrawalsAmount:
										valuesToSubmit
											?.expectedWithdrawalsAmount?.value,
								},
							]
						: [],
				loans:
					Object.entries(data.loans).length !== 0
						? [
								{
									...customer.loans[0],
									benificialOwner:
										valuesToSubmit?.benificialOwner?.value,
									purposeOfLoan:
										valuesToSubmit?.purposeOfLoan?.value,
									purposeOfLoanOther:
										valuesToSubmit?.purposeOfLoanOther
											?.value,
								},
							]
						: [],
				employments: [
					{
						...customer.employments[0],
						occupationType: valuesToSubmit?.occupationType?.value,
						income: valuesToSubmit?.income.value,
					},
				],

				taxCountry:
					data.customerType === CustomerForm.LoanForm
						? data.taxCountry
						: valuesToSubmit?.taxCountry?.value,
				taxCountryOther:
					data.customerType === CustomerForm.LoanForm
						? data.taxCountryOther
						: valuesToSubmit?.taxCountryOther?.value,
				tin: valuesToSubmit?.tin?.value,
				fatca: valuesToSubmit?.fatca?.value,

				address: {
					...customer.address,
					street: valuesToSubmit?.street?.value,
					houseNumber: valuesToSubmit?.houseNumber?.value,
					apartmentNumber: valuesToSubmit?.apartmentNumber?.value,
					country: valuesToSubmit?.country?.value,
					postalCode: valuesToSubmit.postalCode?.value,
					city: valuesToSubmit?.city?.value,
					region: valuesToSubmit?.region?.value,
					shortAddress: valuesToSubmit?.shortAddress?.value,
				},

				phoneNumber: valuesToSubmit?.phoneNumber?.value,
				email: valuesToSubmit?.email?.value,

				politicallyExposedPerson:
					valuesToSubmit?.politicallyExposedPerson?.value,
				connectedToPep: valuesToSubmit?.connectedToPep?.value,
			})
		},
		[customer, data],
	)

	useEffect(
		() => {
			if (valuesToSubmit) {
				updateCustomerObj(valuesToSubmit)
			}
			return () => {}
		},
		[valuesToSubmit], //eslint-disable-line
	)

	const submit = async () => {
		setLoading(true)
		setError(false)

		try {
			customerDataApiService
				.updateKycCustomerData(customer!)
				.then(() => setShowKycModal(false))
		} catch (error) {
			console.warn(error)
			setError(true)
		}
		if (!error) {
			setShowThankYouPage(true)
			saveCustomerToSessionStorage(customer)
		}
	}

	useEffect(() => {
		console.log('Values', valuesToSubmit)
		console.log('Errors', errors)
	}, [errors, valuesToSubmit])

	return (
		<React.Fragment>
			{showKycModal ? (
				<Modal
					id="root"
					className={`kyc-modal kyc-modal-${appConfig.country}`}
					show={showKycModal}
					animation={false}
					size="lg"
					backdrop="static">
					<Modal.Header className="text-center text-md" />
					<Modal.Title
						className="text-center"
						style={{ textTransform: 'uppercase' }}>
						{t('forms:KycHeader')}
					</Modal.Title>
					<Modal.Body>
						{showThankYouPage ? (
							<>
								<BoxInfo
									css={'success'}
									message={t('ThankYou')}
								/>
							</>
						) : (
							<div className="kyc-description">
								{t('forms:KycDescriptionText')}
							</div>
						)}

						{formSchema && !loading ? (
							<React.Fragment>
								<FormBuilder
									formSchema={formSchema}
									apiError={undefined}
									showErrorsInForm={true}
									showSubmitButton={true}
									onSubmit={() => submit()}
								/>
								{error ? (
									<div>{t('UpdatingCustomerInfoError')}</div>
								) : null}
							</React.Fragment>
						) : (
							<CircleSpinner />
						)}
					</Modal.Body>
				</Modal>
			) : null}
		</React.Fragment>
	)
}

export const KycWrapper = () => {
	const { t } = useTranslation(['translation', 'forms'])
	const [data, setData] = useState<IKycData>()
	const [show, setShow] = useState(false)
	// eslint-disable-next-line
	const [error, setError] = useState<Error>()
	const { toggle } = useContext(AppConfigContext)
	const { customerData, isCard } = useContext(SspContext)

	async function getKycData() {
		if (isCard?.isCard) {
			return
		}

		if (sessionStorage.getItem(CookieType.KycRenewal) !== 'false') {
			if (appConfig.country === 'de') {
				await customerDataApiService
					.getKycDataWithNationalId(t, customerData?.nationalId!)
					.then((response) => {
						setData(response)
						if (
							response.customerType !== CustomerForm.DepositForm
						) {
							setShow(false)
						} else {
							setShow(
								sessionStorage.getItem(
									CookieType.KycRenewal,
								) === 'true',
							)
						}
					})
					.catch((e) => setError(getErrorWithFullMessage(e)))
			} else {
				await customerDataApiService
					.getKycData(t)
					.then((response) => {
						setData(response)
						if (
							appConfig.country === 'no' &&
							response.customerType !== CustomerForm.DepositForm
						) {
							setShow(false)
						} else {
							setShow(
								sessionStorage.getItem(
									CookieType.KycRenewal,
								) === 'true',
							)
						}
					})
					.catch((e) => setError(getErrorWithFullMessage(e)))
			}
		}
	}

	useEffect(
		() => {
			if (isCard?.isCard) {
				setShow(false)
				return
			} else {
				toggle.includes(Toggle.kyc) ? getKycData() : setShow(false)
			}
		},
		[isCard?.isCard], //eslint-disable-line
	)

	return (
		<div id="kyc-modal-wrapper">
			{show && (
				<KycModal
					showModal={true}
					data={data!}
					handleClose={() => {
						setShow(false)
					}}
				/>
			)}
		</div>
	)
}
const saveCustomerToSessionStorage = (customer: IKycData) => {
	let oldCustomerData = sessionStorage.getItem(getCustomerInfoCookieName())
	const defaultCustomerFlags = {
		noAdvertisement: false,
		noDataConsent: false,
	}
	let customerFlags = defaultCustomerFlags

	let customerOldInfo: ICustomer = {
		personalInfo: {
			country: '',
			firstName: '',
			lastName: '',
			middleName: '',
			ssn: '',
		},
		address: {
			city: '',
			street: '',
			street2: '',
			zip: '',
			customerCountry: '',
		},
		invoiceAddress: {
			city: '',
			street: '',
			street2: '',
			zip: '',
			customerCountry: '',
		},
		contacts: {
			cellPhone: '',
			email: '',
			homePhone: '',
		},
		customerFlags: {
			noAdvertisement: false,
			noDataConsent: false,
		},
	}

	if (oldCustomerData) {
		customerOldInfo = JSON.parse(oldCustomerData)
		customerFlags = customerOldInfo.customerFlags || defaultCustomerFlags
	}

	const customerInfoData: ICustomer = {
		personalInfo: {
			country: mapCountryCode(
				customerOldInfo.personalInfo.country?.toString() || '',
			),
			firstName:
				customerOldInfo.personalInfo?.firstName || customer.firstName,
			lastName:
				customerOldInfo.personalInfo?.lastName || customer.lastName,
			middleName: customerOldInfo.personalInfo?.middleName || '',
			ssn: customerOldInfo.personalInfo?.ssn || customer.ssn,
		},
		address: {
			city: customer.address?.city || customerOldInfo.address?.city,
			street: customer.address?.street || customerOldInfo.address?.street,
			street2: customerOldInfo.address?.street2 || '',
			zip: customer.address?.postalCode || customerOldInfo.address?.zip,
			customerCountry:
				customer.address?.country?.toString() ||
				customerOldInfo.address?.customerCountry,
		},
		contacts: {
			cellPhone:
				customer.phoneNumber || customerOldInfo.contacts.cellPhone,
			email: customer.email || customerOldInfo.contacts.email,
			homePhone:
				customerOldInfo.contacts?.homePhone ||
				customerOldInfo.contacts.homePhone ||
				'',
		},

		invoiceAddress: undefined,
		customerFlags: customerFlags,
	}

	sessionStorage.setItem(
		getCustomerInfoCookieName(),
		JSON.stringify(customerInfoData),
	)
}

const mapCountryCode = (country: string): CountryA3 | undefined => {
	switch (country) {
		case 'EE':
			return CountryA3.ee
		case 'LT':
			return CountryA3.lt
		case 'LV':
			return CountryA3.lv
		case 'FI':
			return CountryA3.fi
		default:
			return undefined
	}
}
